import { routeConfig as mfValidationsRouteConfig } from '@klarna/mf-validations/src/routeConfig';
import { ValidateInterface } from '@klarna/mf-validations/src/Validate';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import {
  isMfValidationOptionValue,
  MfValidation,
} from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { getValueByPath, walkInPath } from '@mjolnir/mf-fields-ts/src/types/utils';

export class MfValidationsValidator {
  private readonly validator: ValidateInterface<unknown> | undefined;
  private readonly preparedOptions:
    | {
        [index: string]:
          | string
          | boolean
          | number
          | string[]
          | (() => string | boolean | number | string[]);
      }
    | undefined;

  private cachedErrorMessage?: string;

  constructor(mfValidation: MfValidation, currentPayload: () => Payload, pathToField: string) {
    const pathParts = (mfValidation?.pathToValidation || '/').split('/');
    const mfValidationsConfig = Array.isArray(mfValidationsRouteConfig)
      ? mfValidationsRouteConfig.find(r => r.domain === pathParts[0] && r.key === pathParts[1])
      : undefined;
    this.validator = mfValidationsConfig?.validate;
    this.preparedOptions =
      typeof mfValidation.options === 'undefined'
        ? undefined
        : Object.keys(mfValidation.options).reduce((acc, key) => {
            const option = mfValidation.options?.[key];
            if (isMfValidationOptionValue(option)) {
              return { ...acc, [key]: option.value };
            } else {
              // performance: we want to calculate the target path in the constructor once and use it many times in buildOptions() below
              if (!pathToField) {
                // TODO MOE-4238 remove this error message, when MOE-4238 is done
                // eslint-disable-next-line no-console
                console.error(
                  'skipping mfValidation option because pathToField is missing',
                  mfValidation,
                );
                return { ...acc };
              } else {
                const path = option?.pathToValue.includes('..')
                  ? walkInPath(pathToField, option.pathToValue)
                  : option?.pathToValue;
                return {
                  ...acc,
                  [key]: path ? () => getValueByPath(currentPayload(), path) : undefined,
                };
              }
            }
          }, {});

    this.callback = this.callback.bind(this);
    this.errorMessage = this.errorMessage.bind(this);
    this.buildOptions = this.buildOptions.bind(this);
  }

  buildOptions(): object | undefined {
    if (typeof this.preparedOptions === 'undefined') {
      return this.preparedOptions;
    }

    return Object.keys(this.preparedOptions).reduce((acc, key) => {
      const valueOrFunction = this.preparedOptions?.[key];
      return typeof valueOrFunction === 'function'
        ? { ...acc, [key]: valueOrFunction() }
        : { ...acc, [key]: valueOrFunction };
    }, {});
  }

  callback(value: any): boolean {
    if (!value || !this.validator) {
      return true;
    }

    const result = this.validator(value, this.buildOptions());
    if (typeof result.error !== 'undefined') {
      this.cachedErrorMessage = result.error.message;
      return false;
    }
    return true;
  }

  errorMessage() {
    // performance: error message is cached so that the validation is not evaluated twice when callback() and errorMessage() are called in sequence
    return this.cachedErrorMessage;
  }
}
